import PropTypes from 'prop-types';
import {
  Box,
  Button,
  Dialog,
  DialogActions,
  DialogContent,
  DialogTitle,
  FormHelperText,
} from '@material-ui/core';
import { LoadingButton } from '@material-ui/lab';
import { FC } from 'react';
import { useFormik } from 'formik';
import * as Yup from 'yup';
import { useMutation, useQueryClient } from 'react-query';
import toast from 'react-hot-toast';
import { InputField } from '../../../../components/input-field';
import {
  JsonField,
  isValidJson,
  parseJsonOrThrow,
  stringifyJsonForEditor,
} from '../../../../components/json-field';
import { Item } from '../../../../types/item';
import { useAuth } from '../../../../hooks/use-auth';
import { useAxios } from '../../../../hooks/use-axios';
import { ResponseData } from '../../../../types/axios';

interface ItemEditDetailsDialogProps {
  open: boolean;
  onClose: () => void;
  item: Item;
}

export const ItemEditDetailsDialog: FC<ItemEditDetailsDialogProps> = ({
  open,
  onClose,
  item,
  ...other
}) => {
  const queryClient = useQueryClient();
  const { axios } = useAxios();
  const { tenant } = useAuth();

  const updateMutation = useMutation(async (updates: Partial<Item>) => {
    if (!tenant) {
      throw new Error('Tenant is missing');
    }

    const url = `/items/${item.id}`;

    // The item edit endpoint derives the tenant from the item itself and its input
    // schema is strict (no tenantId), so only send the editable fields.
    return axios.put<ResponseData<Item>>(url, updates);
  });

  const initialValues = {
    title: item.title || '',
    // Whole publicMetadata is edited as free-form JSON in this dialog (like the SKU form).
    publicMetadata: stringifyJsonForEditor(item.publicMetadata),
  };

  const formik = useFormik({
    enableReinitialize: true,
    initialValues: {
      ...initialValues,
      submit: null,
    },
    validationSchema: Yup.object().shape({
      title: Yup.string().required('Title is required'),
      publicMetadata: Yup.string().test('is-json', 'Invalid JSON', isValidJson),
    }),
    onSubmit: async (values, helpers) => {
      const { publicMetadata, title } = values;

      const newItem: Partial<Item> = {
        title,
        publicMetadata: parseJsonOrThrow(publicMetadata) as Item['publicMetadata'],
      };

      updateMutation.mutate(newItem, {
        onSuccess: () => {
          toast.success(`Item's properties were updated`);
          queryClient.invalidateQueries('item');
          onClose();
          helpers.setStatus({ success: true });
        },
        onError: (err: any) => {
          helpers.setStatus({ success: false });
          helpers.setErrors({ submit: err?.response?.data?.error?.message || err?.message });
        },
        onSettled: () => {
          helpers.setSubmitting(false);
        },
      });
    },
  });

  return (
    <Dialog
      onClose={onClose}
      open={open}
      PaperProps={{
        sx: {
          width: '100%',
        },
      }}
      TransitionProps={{
        onExited: () => formik.resetForm(),
      }}
      {...other}
    >
      <form onSubmit={formik.handleSubmit}>
        <DialogTitle>Edit Item Properties</DialogTitle>
        <DialogContent>
          <Box
            sx={{
              display: 'grid',
              gap: 2,
              gridAutoFlow: 'row',
            }}
          >
            {/* Title */}
            <Box sx={{ mb: 3 }}>
              <InputField
                error={Boolean(formik.touched.title && formik.errors.title)}
                fullWidth
                helperText={formik.touched.title && formik.errors.title}
                label="Title"
                name="title"
                onBlur={formik.handleBlur}
                onChange={formik.handleChange}
                value={formik.values.title}
                required
              />
            </Box>

            {/* Metadata (free-form JSON) */}
            <JsonField
              label="Metadata"
              name="publicMetadata"
              value={formik.values.publicMetadata}
              onBlur={formik.handleBlur}
              onChange={formik.handleChange}
              error={Boolean(formik.touched.publicMetadata && formik.errors.publicMetadata)}
              helperText={formik.touched.publicMetadata && formik.errors.publicMetadata}
              placeholder="{}"
            />
          </Box>
          {formik.errors.submit && (
            <FormHelperText error sx={{ mb: 2 }}>
              {formik.errors.submit}
            </FormHelperText>
          )}
        </DialogContent>
        <DialogActions>
          <Button color="primary" onClick={onClose} variant="text">
            Cancel
          </Button>
          <LoadingButton
            loading={formik.isSubmitting}
            color="primary"
            type="submit"
            size="large"
            variant="contained"
          >
            Save
          </LoadingButton>
        </DialogActions>
      </form>
    </Dialog>
  );
};

ItemEditDetailsDialog.defaultProps = {
  open: false,
};

ItemEditDetailsDialog.propTypes = {
  onClose: PropTypes.func,
  open: PropTypes.bool,
  item: PropTypes.any,
};
